/*
 * ld-size-guide.js
 * Opens/closes the size guide rendered by snippets/ld-size-guide-modal.liquid.
 * - Native <dialog>.showModal() when available; otherwise a light fallback (open attribute).
 * - Esc closes, Tab/Shift+Tab stay inside the dialog, focus returns to the link that opened it.
 * - Click on the dark area around the panel closes it.
 * - Event delegation on document: works for markup added later and is safe if loaded twice.
 * - Any in-page link to the dialog id (e.g. <a href="#LdSizeGuide">) also opens it.
 */
(function () {
  'use strict';

  if (window.ldSizeGuide) return;

  var root = document.documentElement;
  root.classList.add('ld-sg-js');

  var FOCUSABLE = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    'iframe',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var active = null; // { dialog, opener, native }
  var downOnBackdrop = false;

  function isGuideDialog(el) {
    return !!(el && el.hasAttribute && el.hasAttribute('data-ld-sg-dialog'));
  }

  function focusEl(el) {
    if (!el || typeof el.focus !== 'function') return;
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      el.focus();
    }
  }

  function getFocusable(dialog) {
    var nodes = dialog.querySelectorAll(FOCUSABLE);
    var list = [];
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].offsetWidth || nodes[i].offsetHeight || nodes[i].getClientRects().length) {
        list.push(nodes[i]);
      }
    }
    return list;
  }

  function openDialog(dialog, opener) {
    if (!isGuideDialog(dialog)) return;
    if (active && active.dialog === dialog) return;
    if (active) closeDialog();

    var native = false;
    if (typeof dialog.showModal === 'function') {
      try {
        if (!dialog.open) dialog.showModal();
        native = true;
      } catch (e) {
        native = false;
      }
    }
    if (!native) {
      // Fallback: move to <body> so no transformed ancestor can trap the fixed overlay.
      if (dialog.parentNode !== document.body) document.body.appendChild(dialog);
      dialog.setAttribute('open', '');
    }

    active = { dialog: dialog, opener: opener || document.activeElement, native: native };
    root.classList.add('ld-sg-lock');

    var body = dialog.querySelector('.ld-sg__body');
    if (body) body.scrollTop = 0;
    focusEl(dialog.querySelector('[data-ld-sg-initial-focus]') || getFocusable(dialog)[0]);
  }

  function finishClose(dialog) {
    var opener = null;
    if (active && (!dialog || active.dialog === dialog)) {
      dialog = active.dialog;
      opener = active.opener;
      active = null;
    } else if (dialog) {
      opener = document.getElementById(dialog.id + '-trigger');
    } else {
      return;
    }
    root.classList.remove('ld-sg-lock');
    if (window.location.hash === '#' + dialog.id && window.history && window.history.replaceState) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    if (opener && document.body.contains(opener)) focusEl(opener);
  }

  function closeDialog(dialog) {
    dialog = dialog || (active && active.dialog);
    if (!dialog) return;
    if (dialog.open && typeof dialog.close === 'function') {
      try {
        dialog.close();
      } catch (e) {
        dialog.removeAttribute('open');
      }
    } else {
      dialog.removeAttribute('open');
    }
    finishClose(dialog);
  }

  function trapTab(event) {
    var list = getFocusable(active.dialog);
    if (!list.length) {
      event.preventDefault();
      return;
    }
    var first = list[0];
    var last = list[list.length - 1];
    var current = document.activeElement;
    var outside = !active.dialog.contains(current);
    if (event.shiftKey && (current === first || outside)) {
      event.preventDefault();
      focusEl(last);
    } else if (!event.shiftKey && (current === last || outside)) {
      event.preventDefault();
      focusEl(first);
    }
  }

  function openFromHash() {
    var id = window.location.hash.slice(1);
    if (!id) return;
    try {
      id = decodeURIComponent(id);
    } catch (e) {
      return;
    }
    var dialog = document.getElementById(id);
    if (isGuideDialog(dialog)) openDialog(dialog, document.getElementById(id + '-trigger'));
  }

  document.addEventListener(
    window.PointerEvent ? 'pointerdown' : 'mousedown',
    function (event) {
      downOnBackdrop = !!active && event.target === active.dialog;
    },
    true
  );

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var opener = target.closest('[data-ld-sg-open]');
    if (opener) {
      var dialog = document.getElementById(opener.getAttribute('data-ld-sg-open'));
      if (isGuideDialog(dialog)) {
        event.preventDefault();
        openDialog(dialog, opener);
      }
      return;
    }

    var closer = target.closest('[data-ld-sg-close]');
    var owner = closer ? closer.closest('[data-ld-sg-dialog]') : null;
    if (owner) {
      event.preventDefault();
      closeDialog(owner);
      return;
    }

    if (active) {
      if (target === active.dialog && downOnBackdrop) closeDialog();
      return;
    }

    var link = target.closest('a[href^="#"]');
    if (link) {
      var linked = document.getElementById(link.getAttribute('href').slice(1));
      if (isGuideDialog(linked)) {
        event.preventDefault();
        openDialog(linked, link);
      }
    }
  });

  document.addEventListener('keydown', function (event) {
    var key = event.key;
    if (active) {
      if (key === 'Escape' || key === 'Esc' || event.keyCode === 27) {
        event.preventDefault();
        closeDialog();
        return;
      }
      if (key === 'Tab' || event.keyCode === 9) {
        trapTab(event);
        return;
      }
    }
    // Space activates the role="button" links (Enter already works natively on links).
    if (key === ' ' || key === 'Spacebar' || event.keyCode === 32) {
      var t = event.target;
      if (t && t.matches && t.matches('a[role="button"][data-ld-sg-open], a[role="button"][data-ld-sg-close]')) {
        event.preventDefault();
        t.click();
      }
    }
  });

  // Native dialogs can also be closed by the browser (e.g. Android back gesture).
  document.addEventListener(
    'close',
    function (event) {
      if (active && event.target === active.dialog) finishClose(active.dialog);
    },
    true
  );

  // Theme editor: show the dialog while a size row/step inside it is selected.
  document.addEventListener('shopify:block:select', function (event) {
    var dialog = event.target && event.target.closest ? event.target.closest('[data-ld-sg-dialog]') : null;
    if (dialog) openDialog(dialog, document.getElementById(dialog.id + '-trigger'));
  });
  document.addEventListener('shopify:block:deselect', function (event) {
    if (active && active.dialog.contains(event.target)) closeDialog();
  });
  document.addEventListener('shopify:section:unload', function (event) {
    if (active && event.target.contains(active.dialog)) closeDialog();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', openFromHash);
  } else {
    openFromHash();
  }
  window.addEventListener('hashchange', openFromHash);

  window.ldSizeGuide = {
    open: function (id) {
      var dialog = id ? document.getElementById(id) : document.querySelector('[data-ld-sg-dialog]');
      openDialog(dialog, document.activeElement);
    },
    close: function () {
      closeDialog();
    }
  };
})();
